
import React, { useEffect, useState } from 'react'
import BossDialog from '../../components/BossDialog';
import { EditPageHeader } from '../../components/EditPageHeader';
import { useTranslation } from '../../locale/useTranslation';
import { utility } from '../../framework/utility/utilityProvider';
import { ENTITYNAME, LOGEVENT, MODULE, SLOT_TYPE, TOAST_TYPES } from '../../framework/constant/constant';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import ToastNotification from '../../components/ToastNotification';
import * as API from "../../framework/API/api";
import moment from 'moment';
import ActionButton from '../../framework/forms/helpercomponents/buttons/ActionButton';
import CollectionSelection from '../../framework/forms/CollectionSelection';
import { TimePickerWithFormat } from '../../framework/forms/helpercomponents/timepicker/TimePickerWithFormat';

const ProgramScheduleSlotEditForm = (props) => {
    const lang = useTranslation();

    const blankDataItem = {
        ...props?.item,
        SID: props?.item?.SID ?? 0,
        Channel: props?.item?.Channel ?? {},
        Slot: props?.item?.Slot ?? {},
        Title: props?.item?.Slot?.Title ?? "",
        CustomTitle: props?.item?.Slot?.CustomTitle ?? "",
        Description: props?.item?.Slot?.Description ?? "",
        CustomDescription: props?.item?.Slot?.CustomDescription ?? "",
        Code: props?.item?.Slot?.Code ?? "",
        Duration: props?.item?.Slot?.Duration ? utility.convertMilisecondsToStringWithFrames(props?.item?.Slot?.Duration) : "00:00:00:00",
        Genre: props?.item?.Slot?.Genre ?? {},
        ProductionYear: props?.item?.Slot?.ProductionYear ?? "",
        PGRating: props?.item?.Slot?.PGRating ?? {},
        TargetAudience: props?.item?.Slot?.TargetAudience ?? {},
        ProductionType: props?.item?.Slot?.ProductionType ?? {},
        BgColor: props?.item?.BgColor || props?.item?.Slot?.BgColor || "#4559bf",
        Breaks: props?.item?.Slot?.Breaks ?? 0,
        SlotDateTime: (props?.item?.SlotDateTime || props?.item?.SlotStart)
            ? moment.utc(props?.item?.SlotDateTime || props?.item?.SlotStart).format('YYYY-MM-DDTHH:mm')
            : moment.utc().format('YYYY-MM-DDTHH:mm'),
        Remarks: props?.item?.Remarks ?? "",
        Archive: props?.item?.Archive ?? false
    };

    const [dataItem, setDataItem] = useState(blankDataItem);
    const [channel, setChannel] = useState([]);
    const [genres, setGenres] = useState([]);
    const [targetAudiences, setTargetAudiences] = useState([]);
    const [productionTypes, setProductionTypes] = useState([]);
    const [pGRatings, setPGRatings] = useState([]);
    const [slotTypes, setSlotTypes] = useState([]);
    const [openSlotSelection, setOpenSlotSelection] = useState(false);

    const onChange = (e) => {
        if (e.target.name === "Archive") {
            setDataItem((prev) => ({ ...prev, Archive: !prev.Archive }));
        }
        else {
            setDataItem((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        }
    };

    useEffect(() => {
        loadCombo();
    }, [])

    const loadCombo = async () => {
        const channelRes = await API.getDataLookup(ENTITYNAME.Channel, { sort: { FullChannelName: 1 } });
        const channelsList = channelRes?.data || [];
        setChannel(channelsList);

        // If dataItem already has Channel with SID or _id, ensure full channel object from lookup list is selected
        if (blankDataItem?.Channel && channelsList.length > 0) {
            const matched = channelsList.find((c) =>
                (blankDataItem.Channel._id && c._id === blankDataItem.Channel._id) ||
                (blankDataItem.Channel.SID != null && c.SID === blankDataItem.Channel.SID)
            );
            if (matched) {
                setDataItem((prev) => ({ ...prev, Channel: matched }));
            }
        }

        const genreRes = await API.getDataLookup(ENTITYNAME.Genre, { sort: { Description: 1 } });
        setGenres(genreRes.data);
        const targetAudienceRes = await API.getDataLookup(ENTITYNAME.Audience, { sort: { Description: 1 } });
        setTargetAudiences(targetAudienceRes.data);
        const productionTypeRes = await API.getDataLookup(ENTITYNAME.ProductionType, { sort: { Description: 1 } });
        setProductionTypes(productionTypeRes.data);
        const pGRatingRes = await API.getDataLookup(ENTITYNAME.PGRating, { sort: { Description: 1 } });
        setPGRatings(pGRatingRes.data);
        const slotTypeRes = Object.keys(SLOT_TYPE).map((x) => {
            return {
                _id: SLOT_TYPE[x],
                Description: x
            }
        });
        setSlotTypes(slotTypeRes);
    }

    const isValid = () => {
        if (dataItem.Channel == undefined || Object.keys(dataItem.Channel).length == 0) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_channel_error_message });
            return false;
        }
        if (dataItem.Slot == undefined || Object.keys(dataItem.Slot).length == 0) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_slot_error_message ?? "Please select Slot" });
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (isValid()) {
            const slotDurationVal = dataItem?.Duration ? utility.convertStringWithFramesToMilliseconds(dataItem?.Duration) : (dataItem?.Slot?.Duration || 0);
            const slotDateTimeVal = dataItem.SlotDateTime ? moment.utc(dataItem.SlotDateTime).valueOf() : null;
            const saveData = {
                _id: dataItem._id,
                SID: dataItem.SID,
                Title: dataItem?.Title,
                Description: dataItem?.Description || dataItem?.Title,
                Duration: slotDurationVal,
                SlotStart: slotDateTimeVal,
                SlotEnd: slotDateTimeVal != null ? (slotDateTimeVal + slotDurationVal) : null,
                BgColor: dataItem?.BgColor || "#4559bf",
                Channel: dataItem?.Channel && Object.keys(dataItem.Channel).length > 0 ? {
                    _id: dataItem?.Channel?._id,
                    SID: dataItem?.Channel?.SID,
                    FullChannelName: dataItem?.Channel?.FullChannelName
                } : {},
                Slot: dataItem?.Slot && Object.keys(dataItem?.Slot).length > 0 ? {
                    _id: dataItem?.Slot?._id,
                    SID: dataItem?.Slot?.SID,
                    Title: dataItem?.Title,
                    CustomTitle: dataItem?.CustomTitle,
                    Description: dataItem?.Description,
                    CustomDescription: dataItem?.CustomDescription,
                    Code: dataItem?.Code,
                    Duration: slotDurationVal,
                    Genre: dataItem?.Genre && Object.keys(dataItem?.Genre).length > 0 ? {
                        _id: dataItem?.Genre?._id,
                        SID: dataItem?.Genre?.SID,
                        Description: dataItem?.Genre?.Description
                    } : {},
                    ProductionYear: dataItem?.ProductionYear,
                    PGRating: dataItem?.PGRating && Object.keys(dataItem?.PGRating).length > 0 ? {
                        _id: dataItem?.PGRating?._id,
                        Description: dataItem?.PGRating?.Description
                    } : {},
                    TargetAudience: dataItem?.TargetAudience && Object.keys(dataItem?.TargetAudience).length > 0 ? {
                        _id: dataItem?.TargetAudience?._id,
                        Description: dataItem?.TargetAudience?.Description
                    } : {},
                    ProductionType: dataItem?.ProductionType && Object.keys(dataItem?.ProductionType).length > 0 ? {
                        _id: dataItem?.ProductionType?._id,
                        Description: dataItem?.ProductionType?.Description
                    } : {},
                    SlotType: dataItem?.SlotType && Object.keys(dataItem?.SlotType).length > 0 ? {
                        _id: dataItem?.SlotType?._id,
                        Description: dataItem?.SlotType?.Description
                    } : {},
                    BgColor: dataItem?.BgColor || "#4559bf",
                    Breaks: dataItem?.Breaks ?? 0,
                } : {},
                SlotDateTime: slotDateTimeVal,
                Remarks: dataItem?.Remarks ?? "",
                Archive: dataItem.Archive ?? false
            };
            console.log(saveData);
            let res = await API.saveProgramScheduleSlot(saveData);
            if (res.success) {
                let logData = { event: res.message == "updated" ? LOGEVENT.UPDATE : LOGEVENT.INSERT, module: MODULE.PROGRAM_SCHEDULE_SLOT, data: res.data, message: res.message };
                API.SaveLogs(logData);
                utility.deleteLocalStorageItem(ENTITYNAME.ProgramScheduleSlot);
                if (props.cancelEdit) props.cancelEdit(true);
                else if (props.onClose) props.onClose(true);
                if (props.refresh) props.refresh();
            } else {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: res.message });
            }
        }
    };

    const onSlotSelected = (dataList) => {
        if (dataList && dataList.length > 0) {
            const selectedSlot = dataList[0];
            setDataItem((prev) => ({
                ...prev,
                Slot: selectedSlot,
                Title: selectedSlot?.Title ?? "",
                CustomTitle: selectedSlot?.CustomTitle ?? "",
                Description: selectedSlot?.Description ?? "",
                CustomDescription: selectedSlot?.CustomDescription ?? "",
                Code: selectedSlot?.Code ?? "",
                Duration: selectedSlot?.Duration ? utility.convertMilisecondsToStringWithFrames(selectedSlot?.Duration) : "00:00:00:00",
                Genre: selectedSlot?.Genre ?? {},
                ProductionYear: selectedSlot?.ProductionYear ?? "",
                PGRating: selectedSlot?.PGRating ?? {},
                TargetAudience: selectedSlot?.TargetAudience ?? {},
                ProductionType: selectedSlot?.ProductionType ?? {},
                SlotType: selectedSlot?.SlotType ?? {},
                BgColor: selectedSlot?.BgColor || prev.BgColor || "#4559bf",
                Breaks: selectedSlot?.Breaks ?? 0,
            }));
        }
        setOpenSlotSelection(false);
    };

    const handleClose = () => {
        if (props.cancelEdit) props.cancelEdit(false);
        else if (props.onClose) props.onClose(false);
    };

    const onChangeSlot = (e) => {
        setDataItem((prev) => ({ ...prev, Slot: {}, Title: "", CustomTitle: "", Description: "", CustomDescription: "", Code: "", Duration: "00:00:00:00", Genre: {}, ProductionYear: "", PGRating: {}, TargetAudience: {}, ProductionType: {}, SlotType: {}, BgColor: "#4559bf", Breaks: 0 }));
    }

    return (
        <>
            <BossDialog
                title={props.item.SID > 0 ? props.item.Description : lang.program_schedule_slot_dialog_header}
                onClose={handleClose}
                width={"820px"}
            >
                <EditPageHeader title={""} onSubmit={handleSubmit} onCancel={handleClose} showTitle={false} />
                <div className="row">
                    <div className="col-12">
                        <div className="row mt-2">
                            <div className="col-6">
                                <label>{lang.channel_label} *</label>
                                <DropDownList
                                    style={{ backgroundColor: "white" }}
                                    data={channel}
                                    name="Channel"
                                    textField="FullChannelName"
                                    dataItemKey="_id"
                                    value={dataItem.Channel}
                                    onChange={onChange}
                                />
                            </div>
                            <div className="col-6">
                                <div className="form-group">
                                    <label>{lang.slot_label} *</label>
                                    <div style={{ display: "flex" }}>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={dataItem.Slot?.Title ?? ""}
                                            onChange={() => ""}
                                            disabled={true}
                                            placeholder="Select Slot..."
                                        />
                                        <ActionButton
                                            style={{ marginLeft: "5px", height: "30px" }}
                                            btnColor={"success"}
                                            title={lang.select_button_tooltip}
                                            name={"..."}
                                            onClick={() => setOpenSlotSelection(true)}
                                        />
                                        <ActionButton
                                            style={{ marginLeft: "2px", height: "30px" }}
                                            btnColor={"danger"}
                                            title={lang.clear_button_tooltip}
                                            name={""}
                                            icon={"xmark"}
                                            onClick={onChangeSlot}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-4">
                                <label>{lang.title_label}</label>
                                <input type="text" className="form-control form-control-sm" name="Title" value={dataItem.Title} onChange={onChange} />
                            </div>
                            <div className="col-4">
                                <label>{lang.custom_title}</label>
                                <input type="text" className="form-control form-control-sm" name="CustomTitle" value={dataItem.CustomTitle} onChange={onChange} />
                            </div>
                            <div className="col-4">
                                <label>{lang.code_label}</label>
                                <input type="text" className="form-control form-control-sm" name="Code" value={dataItem.Code} onChange={onChange} />
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-4">
                                <label>{lang.description_label}</label>
                                <input type="text" className="form-control form-control-sm" name="Description" value={dataItem.Description} onChange={onChange} />
                            </div>
                            <div className="col-4">
                                <label>{lang.custom_description_label}</label>
                                <input type="text" className="form-control form-control-sm" name="CustomDescription" value={dataItem.CustomDescription} onChange={onChange} />
                            </div>
                            <div className="col-4">
                                <label>{lang.duration_label}</label>
                                <TimePickerWithFormat
                                    className="form-control form-control-sm"
                                    name="Duration"
                                    value={dataItem.Duration}
                                    onChange={onChange}
                                />
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-4">
                                <label>{lang.genre_label}</label>
                                <DropDownList
                                    style={{ backgroundColor: "white" }}
                                    data={genres}
                                    name="Genre"
                                    textField="Description"
                                    dataItemKey="_id"
                                    value={dataItem.Genre}
                                    onChange={onChange}
                                />
                            </div>
                            <div className="col-4">
                                <label>{lang.pg_rating_label}</label>
                                <DropDownList
                                    style={{ backgroundColor: "white" }}
                                    data={pGRatings}
                                    name="PGRating"
                                    textField="Description"
                                    dataItemKey="_id"
                                    value={dataItem.PGRating}
                                    onChange={onChange}
                                />
                            </div>
                            <div className="col-4">
                                <label>{lang.production_year_label}</label>
                                <input type="text" className="form-control form-control-sm" name="ProductionYear" value={dataItem.ProductionYear} onChange={onChange} />
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-4">
                                <label>{lang.target_audience_label}</label>
                                <DropDownList
                                    style={{ backgroundColor: "white" }}
                                    data={targetAudiences}
                                    name="TargetAudience"
                                    textField="Description"
                                    dataItemKey="_id"
                                    value={dataItem.TargetAudience}
                                    onChange={onChange}
                                />
                            </div>
                            <div className="col-4">
                                <label>{lang.production_type_label}</label>
                                <DropDownList
                                    style={{ backgroundColor: "white" }}
                                    data={productionTypes}
                                    name="ProductionType"
                                    textField="Description"
                                    dataItemKey="_id"
                                    value={dataItem.ProductionType}
                                    onChange={onChange}
                                />
                            </div>
                            <div className="col-4">
                                <label>{lang.slot_type_label}</label>
                                <DropDownList
                                    style={{ backgroundColor: "white" }}
                                    data={slotTypes}
                                    name="SlotType"
                                    textField="Description"
                                    dataItemKey="_id"
                                    value={dataItem.SlotType}
                                    onChange={onChange}
                                />
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-4">
                                <label>{lang.slot_date_time_label}</label>
                                <input type="datetime-local" name="SlotDateTime" onChange={onChange} value={dataItem.SlotDateTime} className="form-control form-control-sm" />
                            </div>
                            <div className="col-3">
                                <label style={{ display: "block", marginBottom: "4px", whiteSpace: "nowrap" }}>{lang.background_color_label}</label>
                                <input type="color" name="BgColor" style={{ width: "50px", height: "30px", padding: "2px", cursor: "pointer" }} onChange={onChange} value={dataItem.BgColor || "#4559bf"} className="form-control form-control-sm" />
                            </div>
                            <div className="col-2">
                                <label>{lang.breaks_label}</label>
                                <input type="number" className="form-control form-control-sm" name="Breaks" value={dataItem.Breaks} onChange={onChange} />
                            </div>
                            <div className='col-3' style={{ display: "flex", alignItems: "center", paddingTop: "22px" }}>
                                <input style={{ border: "1px solid grey" }} name={"Archive"} type='checkbox' onChange={onChange} checked={dataItem.Archive} />
                                <label className="ml-2 mb-0">{lang.archive}</label>
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-12">
                                <label>{lang.remark_label}</label>
                                <textarea type="text" className="form-control form-control-sm" name="Remarks" value={dataItem.Remarks} onChange={onChange} />
                            </div>
                        </div>
                    </div>
                </div>
            </BossDialog>
            {openSlotSelection && (<CollectionSelection showCreate={true} path={`/home/slot`} addButtonTitle={lang.select_button_text} title={lang.slot_dialog_header} entityname={ENTITYNAME.Slot} closeForm={() => setOpenSlotSelection(false)} setDataList={onSlotSelected} mode={"single"} width={"50vw"} />)}
        </>
    );
}

export default ProgramScheduleSlotEditForm